import { useState, useEffect } from "react";
import posthog from "posthog-js";
import type { Session } from "@supabase/supabase-js";
import { HeroSection } from "./components/HeroSection";
import { LoadingState } from "./components/LoadingState";
import { AnalysisResults } from "./components/AnalysisResults";
import { PaywallModal, checkPendingPayment } from "./components/PaywallModal";
import { getCreditsInfo, getAuthHeaders } from "./credits";
import { initSupabaseClient, getSession, onAuthStateChange, signInWithGoogle, signOut } from "./lib/supabase-client";
import type { CreditsInfo } from "./credits";
import type { AnalysisResult } from "./scrapers/types";
import "./index.css";

type AppState = "idle" | "loading" | "results" | "error" | "paywall";

export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [appState, setAppState] = useState<AppState>("idle");
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [creditsInfo, setCreditsInfo] = useState<CreditsInfo | null>(null);

  // Auth initialization — fetch config first, then check session
  useEffect(() => {
    let subscription: { unsubscribe: () => void } | null = null;

    initSupabaseClient().then(() => {
      getSession().then((s) => {
        setSession(s);
        setAuthLoading(false);
      });

      subscription = onAuthStateChange((s) => {
        setSession(s);
        setAuthLoading(false);
      });
    });

    return () => subscription?.unsubscribe();
  }, []);

  // When session is established, fetch credits + check pending payment
  useEffect(() => {
    if (!session) return;
    refreshCredits();
    checkPendingPayment().then((verified) => {
      if (verified) refreshCredits();
    });
  }, [session]);

  async function refreshCredits() {
    try {
      setCreditsInfo(await getCreditsInfo());
    } catch {
      // Silently fail — badge will show loading state
    }
  }

  async function handleAnalyze(url: string, jobDescriptionFallback?: string) {
    setAppState("loading");
    setErrorMessage("");
    posthog.capture("analysis_started", {
      url,
      has_fallback: !!jobDescriptionFallback,
    });

    try {
      const headers = await getAuthHeaders();
      const response = await fetch("/api/analyze", {
        method: "POST",
        headers,
        body: JSON.stringify({ url, jobDescriptionFallback }),
      });

      if (response.status === 402) {
        setAppState("paywall");
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error ?? "Analysis failed");
      }

      const result = data as AnalysisResult;
      await refreshCredits();
      setAnalysisResult(result);
      setAppState("results");
      posthog.capture("analysis_completed", {
        verdict: result.verdict,
        score: result.totalScore,
        platform: result.jobData?.platform,
        confidence: result.confidence,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "An unexpected error occurred";
      setErrorMessage(message);
      setAppState("error");
      posthog.capture("analysis_failed", { error_message: message });
    }
  }

  function handleReset() {
    posthog.capture("analysis_reset");
    setAppState("idle");
    setAnalysisResult(null);
    setErrorMessage("");
  }

  async function handlePaymentSuccess() {
    await refreshCredits();
    setAppState("idle");
  }

  async function handleSignOut() {
    await signOut();
    setSession(null);
    setCreditsInfo(null);
    setAppState("idle");
    setAnalysisResult(null);
  }

  // Auth loading state
  if (authLoading) {
    return (
      <div className="min-h-screen min-h-dvh flex items-center justify-center">
        <div
          className="w-5 h-5 rounded-full border-2 animate-spin"
          style={{ borderColor: "rgba(255,255,255,0.1)", borderTopColor: "rgba(255,255,255,0.4)" }}
        />
      </div>
    );
  }

  // Sign-in gate
  if (!session) {
    return (
      <div className="min-h-screen min-h-dvh flex flex-col items-center justify-center px-4 sm:px-6 py-12 sm:py-16 relative overflow-hidden">
        {/* Background radial mesh */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `
              radial-gradient(ellipse 60% 50% at 50% 40%, rgba(255,68,68,0.04) 0%, transparent 70%),
              radial-gradient(ellipse 40% 40% at 30% 60%, rgba(129,140,248,0.03) 0%, transparent 70%)
            `,
          }}
        />

        <div className="relative z-10 w-full max-w-lg flex flex-col items-center">
          {/* Status badge */}
          <span
            className="inline-flex items-center gap-2 sm:gap-2.5 px-3 sm:px-4 py-1.5 rounded-full mb-8 sm:mb-12"
            style={{
              border: "1px solid rgba(255,68,68,0.15)",
              background: "rgba(255,68,68,0.05)",
            }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full animate-pulse-glow"
              style={{ backgroundColor: "#ff4444", boxShadow: "0 0 8px #ff444488" }}
            />
            <span className="mono-label" style={{ color: "rgba(255,68,68,0.6)" }}>
              Ghost Job Detector
            </span>
          </span>

          {/* Headline */}
          <div className="text-center mb-2 sm:mb-3">
            <h1
              style={{ fontFamily: "var(--font-display)", lineHeight: 0.95 }}
              className="text-[clamp(2.75rem,10vw,6.5rem)] font-normal tracking-tight text-white"
            >
              Is this job
            </h1>
            <h1
              style={{
                fontFamily: "var(--font-display)",
                fontStyle: "italic",
                lineHeight: 0.95,
                color: "rgba(255,255,255,0.12)",
              }}
              className="text-[clamp(2.75rem,10vw,6.5rem)] font-normal tracking-tight"
            >
              even real?
            </h1>
          </div>

          {/* Ghost */}
          <div className="text-4xl sm:text-5xl mb-5 sm:mb-6 animate-float select-none" aria-hidden>
            👻
          </div>

          {/* Tagline */}
          <p
            className="text-center text-xs sm:text-sm leading-relaxed mb-8 sm:mb-10 max-w-xs sm:max-w-sm px-2"
            style={{ color: "var(--text-secondary)" }}
          >
            Detect ghost job postings with AI-powered analysis.
            <br className="hidden sm:block" />
            <span className="sm:hidden"> </span>
            Sign in to start investigating.
          </p>

          {/* Google sign-in button */}
          <button
            onClick={() => signInWithGoogle()}
            className="flex items-center justify-center gap-3 px-6 py-3.5 rounded-xl sm:rounded-2xl font-semibold text-sm tracking-wide transition-all duration-300 active:scale-[0.98] w-full max-w-xs"
            style={{
              background: "#fff",
              color: "#08080a",
              cursor: "pointer",
              boxShadow: "0 2px 16px rgba(255,255,255,0.1)",
            }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
              <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
              <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
              <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </button>

          {/* Footer */}
          <p
            className="text-center mt-8 mono-label"
            style={{ color: "var(--text-ghost)" }}
          >
            2 free analyses · no credit card required
          </p>
        </div>
      </div>
    );
  }

  // Authenticated app
  if (appState === "paywall") {
    return <PaywallModal onSuccess={handlePaymentSuccess} />;
  }

  if (appState === "loading") return <LoadingState />;

  if (appState === "results" && analysisResult) {
    return <AnalysisResults result={analysisResult} onReset={handleReset} />;
  }

  // idle or error
  return (
    <HeroSection
      onAnalyze={handleAnalyze}
      error={appState === "error" ? errorMessage : undefined}
      creditsInfo={creditsInfo}
      userEmail={session.user.email ?? ""}
      onSignOut={handleSignOut}
    />
  );
}

export default App;
